import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Link2, Check, X, Briefcase } from 'lucide-react';
import type { RecipientLinkSuggestion } from '@/hooks/email/useEmailRecipientLinks';
import type { EmailComposeLink } from '@/types/email';

interface EmailLinkSuggestionProps {
  suggestions: RecipientLinkSuggestion[];
  onAccept: (link: EmailComposeLink) => void;
  onDismiss: (leadId: string) => void;
  acceptedLinks: EmailComposeLink[];
}

export function EmailLinkSuggestion({
  suggestions,
  onAccept,
  onDismiss,
  acceptedLinks,
}: EmailLinkSuggestionProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (suggestions.length === 0) return null;

  const visible = suggestions.filter(
    (s) => !dismissed.has(s.lead.id) && !acceptedLinks.some((l) => l.lead_id === s.lead.id)
  );

  if (visible.length === 0) return null;

  const handleDismiss = (leadId: string) => {
    setDismissed((prev) => new Set(prev).add(leadId));
    onDismiss(leadId);
  };

  const handleDismissAll = () => {
    visible.forEach((s) => handleDismiss(s.lead.id));
  };

  const rowClass =
    'flex items-center gap-2 px-3 py-1.5 rounded-lg border border-primary/20 bg-primary/5 text-sm animate-in fade-in-0 slide-in-from-top-1 duration-200';

  // Single suggestion
  if (visible.length === 1) {
    const suggestion = visible[0];

    return (
      <div className={rowClass}>
        <Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="font-medium text-foreground truncate">{suggestion.lead.name}</span>
          {suggestion.company && (
            <span className="text-muted-foreground text-xs truncate">
              · {suggestion.company.name}
            </span>
          )}
          {suggestion.deals.length === 1 && (
            <>
              <span className="text-muted-foreground">—</span>
              <Badge className="bg-muted text-muted-foreground text-xs gap-1 shrink-0 border-0">
                <Briefcase className="h-3 w-3 text-muted-foreground" />
                {suggestion.deals[0].title}
              </Badge>
            </>
          )}
          {suggestion.deals.length > 1 && (
            <span className="text-xs text-muted-foreground shrink-0">
              — {suggestion.deals.length} deals
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {suggestion.deals.length === 1 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
              onClick={() =>
                onAccept({
                  lead_id: suggestion.lead.id,
                  deal_id: suggestion.deals[0].id,
                  company_id: suggestion.company?.id,
                  link_source: 'participant_email',
                })
              }
            >
              <Check className="h-3 w-3 mr-1" />
              Vincular
            </Button>
          ) : suggestion.deals.length > 1 ? (
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
                >
                  Ver deals
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 p-1">
                {suggestion.deals.map((deal) => (
                  <button
                    key={deal.id}
                    onClick={() => {
                      onAccept({
                        lead_id: suggestion.lead.id,
                        deal_id: deal.id,
                        company_id: suggestion.company?.id,
                        link_source: 'participant_email',
                      });
                      setPopoverOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors text-left"
                  >
                    <Briefcase className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="truncate">{deal.title}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
              onClick={() =>
                onAccept({
                  lead_id: suggestion.lead.id,
                  company_id: suggestion.company?.id,
                  link_source: 'participant_email',
                })
              }
            >
              <Check className="h-3 w-3 mr-1" />
              Vincular lead
            </Button>
          )}
          <button
            onClick={() => handleDismiss(suggestion.lead.id)}
            className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Ignorar sugestão"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Multiple suggestions — compact summary with popover
  return (
    <div className={rowClass}>
      <Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
      <span className="text-foreground font-medium flex-1">
        {visible.length} contatos encontrados
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
            >
              Ver
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-1">
            <div className="space-y-0.5">
              {visible.map((suggestion) => (
                <div
                  key={suggestion.lead.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-sm"
                >
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className="font-medium text-foreground truncate">
                      {suggestion.lead.name}
                    </span>
                    {suggestion.company && (
                      <span className="text-muted-foreground text-xs truncate">
                        · {suggestion.company.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {suggestion.deals.length === 1 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
                        onClick={() => {
                          onAccept({
                            lead_id: suggestion.lead.id,
                            deal_id: suggestion.deals[0].id,
                            company_id: suggestion.company?.id,
                            link_source: 'participant_email',
                          });
                          setPopoverOpen(false);
                        }}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Vincular
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-primary hover:bg-primary/10"
                        onClick={() => {
                          onAccept({
                            lead_id: suggestion.lead.id,
                            company_id: suggestion.company?.id,
                            link_source: 'participant_email',
                          });
                          setPopoverOpen(false);
                        }}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Vincular
                      </Button>
                    )}
                    <button
                      onClick={() => handleDismiss(suggestion.lead.id)}
                      className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Ignorar sugestão"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button
          onClick={handleDismissAll}
          className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Ignorar todas as sugestões"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
